import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Grid,
  Text,
  Badge,
  Group,
  Avatar,
  Stack,
  Button,
  Divider,
  Card,
  ThemeIcon,
  Alert,
} from '@mantine/core';

import {
  IconMail,
  IconPhone,
  IconCalendar,
  IconClock,
  IconShield,
  IconLock,
  IconLockOpen,
  IconArrowLeft,
  IconEdit,
} from '@tabler/icons-react';
import axios from 'axios';
import { useAppSelector } from '../../../store/hooks';
import { notifications } from '@mantine/notifications';
import { Loader } from '../../../components/common/Loader';
export const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  const { token } = useAppSelector((state) => state.auth);
  const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to fetch user details',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container>
        <Alert color="red">User not found</Alert>
      </Container>
    );
  }

  return (
    <Container size="lg">
      <Button
        leftSection={<IconArrowLeft size={16} />}
        variant="subtle"
        onClick={() => navigate('/admin/users')}
        mb="lg"
      >
        Back to Users
      </Button>

      <Grid>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card withBorder padding="xl" radius="md">
            <Stack align="center" gap="md">
              <Avatar size={120} color="exportGreen" radius={120}>
                {user.fullName.charAt(0).toUpperCase()}
              </Avatar>
              <Title order={3} ta="center">{user.fullName}</Title>
              <Text size="sm" c="dimmed">@{user.username}</Text>
              
              <Group gap="xs">
                {user.roles.map((role: string) => (
                  <Badge key={role} color="exportGreen" size="lg">
                    {role.replace('ROLE_', '')}
                  </Badge>
                ))}
              </Group>

              <Divider my="sm" />

              <Group gap="xs">
                <ThemeIcon color={user.enabled ? 'green' : 'red'} variant="light">
                  {user.enabled ? <IconLockOpen size={16} /> : <IconLock size={16} />}
                </ThemeIcon>
                <Text size="sm">Status: {user.enabled ? 'Active' : 'Locked'}</Text>
              </Group>

              <Button
                fullWidth
                leftSection={<IconEdit size={16} />}
                color="exportGreen"
                onClick={() => navigate('/admin/users')}
              >
                Edit User
              </Button>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 8 }}>
          <Card withBorder padding="xl" radius="md">
            <Title order={4} mb="lg">User Information</Title>
            
            <Stack gap="md">
              <Group>
                <ThemeIcon color="blue" variant="light">
                  <IconMail size={16} />
                </ThemeIcon>
                <div>
                  <Text size="sm" c="dimmed">Email</Text>
                  <Text fw={500}>{user.email}</Text>
                  {user.emailVerified && (
                    <Badge color="green" size="xs">Verified</Badge>
                  )}
                </div>
              </Group>

              <Group>
                <ThemeIcon color="green" variant="light">
                  <IconPhone size={16} />
                </ThemeIcon>
                <div>
                  <Text size="sm" c="dimmed">Phone</Text>
                  <Text fw={500}>{user.phoneNumber}</Text>
                  {user.phoneVerified && (
                    <Badge color="green" size="xs">Verified</Badge>
                  )}
                </div>
              </Group>

              <Group>
                <ThemeIcon color="violet" variant="light">
                  <IconShield size={16} />
                </ThemeIcon>
                <div>
                  <Text size="sm" c="dimmed">Two-Factor Authentication</Text>
                  <Badge color={user.twoFactorEnabled ? 'blue' : 'gray'}>
                    {user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </Group>

              <Group>
                <ThemeIcon color="orange" variant="light">
                  <IconCalendar size={16} />
                </ThemeIcon>
                <div>
                  <Text size="sm" c="dimmed">Member Since</Text>
                  <Text fw={500}>
                    {new Date(user.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Text>
                </div>
              </Group>

              <Group>
                <ThemeIcon color="cyan" variant="light">
                  <IconClock size={16} />
                </ThemeIcon>
                <div>
                  <Text size="sm" c="dimmed">Last Login</Text>
                  <Text fw={500}>
                    {user.lastLoginAt
                      ? new Date(user.lastLoginAt).toLocaleString()
                      : 'Never'
                    }
                  </Text>
                </div>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>
    </Container>
  );
};